// ============================================================
// gateDrawing.js
// Pure geometry helpers for chain link gate CUT SHEETS.
//
// This is fabrication data for the welder — the lengths to cut the frame
// members to — NOT an installation detail. Nothing here knows about posts,
// footings, or ground clearance.
//
// No React, no Firebase, no side effects. All values are in INCHES unless the
// name ends in `Label`, which is a formatted string for the drawing.
// ============================================================

/**
 * Format a length in inches as a feet-inches string: 120 → `10'-0"`,
 * 54 → `4'-6"`. Rounds to the nearest whole inch first.
 *
 * NOT used by the cut sheet — the shop works in plain inches — but kept
 * exported for any future elevation/installation drawing that needs it.
 */
/**
 * Feet-and-inches label: 246 → `20'-6"`, 90 → `7'-6"`.
 *
 * The inches remainder keeps TWO DECIMALS rather than being rounded to a whole
 * inch. It used to round, which silently moved a 240.5" opening to 20'-7" — on
 * a cantilever this is the ONLY dimension on the sheet, so half an inch of
 * rounding error had nothing to contradict it. Whole-inch values are unchanged.
 */
export function formatFeetInches(inches) {
  const raw = parseFloat(inches) || 0;
  const sign = raw < 0 ? '-' : '';
  const abs = Math.abs(raw);
  let feet = Math.floor(abs / 12);
  let rem = round2(abs - feet * 12);
  // A float a hair under a foot boundary would otherwise print as 19'-12".
  if (rem >= 12) { feet += 1; rem = 0; }
  return `${sign}${feet}'-${rem}"`;
}

/**
 * Round to at most two decimals, dropping trailing zeros:
 * 41.25 → 41.25, 46.5 → 46.5, 42 → 42.
 */
const round2 = (value) => Math.round(value * 100) / 100;

/**
 * THE inch label for this module. Two decimals, no trailing zeros:
 * 42 → `42"`, 46.5 → `46.5"`, 41.25 → `41.25"`, 65.5 → `65.5"`.
 *
 * There is deliberately only ONE of these. It used to round to a single
 * decimal, which turned a Commercial 48x48's true 41.25" rail cut into 41.3"
 * — a number the welder has no way to tell from a correct one. A second
 * two-decimal formatter existed alongside it for the one label that had to be
 * exact, which is precisely the trap: the next person adding a label reaches
 * for the wrong one and reintroduces the bug. Nothing wanted one-decimal
 * rounding, so the two are now one.
 *
 * Every cut and every measurement in this file goes through here.
 */
export function formatInches(inches) {
  return `${round2(Math.max(0, parseFloat(inches) || 0))}"`;
}

/**
 * Height label in feet, dropping the inches part when it is zero:
 * 72 → `6'`, 12 → `1'`, 42 → `3'-6"`.
 *
 * Used for the split barbed-wire dimensions. The owner wants the sheet to read
 * 6' + 1' rather than a single 84" figure, even though 84" is the true upright
 * cut length — the fabricator reads the split, the cut list reads the total.
 */
function formatHeightLabel(inches) {
  const abs = Math.max(0, parseFloat(inches) || 0);
  let feet = Math.floor(abs / 12);
  // Two decimals for the same reason as formatFeetInches: a 66.5" fence used
  // to print as 5'-7". Whole-foot heights still read 6' / 1' exactly as before.
  let rem = round2(abs - feet * 12);
  if (rem >= 12) { feet += 1; rem = 0; }
  return rem === 0 ? `${feet}'` : `${feet}'-${rem}"`;
}

// Frame outside diameter by grade, in inches.
const FRAME_OD_IN = {
  Residential: 1.375,
  Commercial: 1.625,
};
const DEFAULT_FRAME_OD_IN = FRAME_OD_IN.Residential;

// ── THE WELDER'S RAIL CUT PIECE (actual size only) ────────────────────────
//
// Under ACTUAL SIZE the sheet prints the width the salesperson entered, which
// is the finished outside measurement the customer approves. The welder needs a
// different number: the RAIL itself, after the two uprights are taken out and
// the notch allowance added back.
//
//   cut = actualWidth - (realOd x 2) + notch
//
// ⚠️ THESE ARE NOT THE SAME NUMBERS AS FRAME_OD_IN ABOVE, AND MUST NOT BE
// "CORRECTED" TO MATCH THEM. FRAME_OD_IN carries the NOMINAL trade sizes —
// 1-3/8" and 1-5/8" as 1.375 and 1.625 — which is what the sheet calls the tube
// and what the existing frame geometry is drawn from. The figures here are the
// REAL measured outside diameters of that same pipe: what the trade calls
// 1-3/8" measures 1.315", and 1-5/8" measures 1.66".
//
// The two differ in OPPOSITE DIRECTIONS, so neither is a safe stand-in for the
// other: nominal is LARGER than real at 1-3/8" (1.375 vs 1.315) and SMALLER at
// 1-5/8" (1.625 vs 1.66). Substituting the nominal figure would cut a
// commercial rail about 1/16" LONG — and a rail cut long does not fit between
// the uprights at all — while cutting a residential rail needlessly short. Two
// different jobs, two different constants, deliberately kept apart.
//
// Keyed by the NOMINAL od (i.e. by FRAME_OD_IN's value), which is what the grade
// resolves to, so there is one lookup rather than a second grade table.
const RAIL_CUT_PIECE_BY_FRAME_OD = {
  // 1-3/8" frame — Residential
  1.375: { realOdIn: 1.315, notchIn: 0.5 },
  // 1-5/8" frame — Commercial
  1.625: { realOdIn: 1.66, notchIn: 0.75 },
  // 2" frame — ⚠️ UNREACHABLE TODAY. The frame size comes from the grade, and
  // no grade maps to a 2" frame, so nothing can select this entry. It is here
  // because the shop's formula sheet has it and the figure would otherwise be
  // lost. It becomes live the moment a frame selector exists — DO NOT add one
  // here; this table is arithmetic, not UI.
  2: { realOdIn: 1.90, notchIn: 0.75 },
};

/**
 * Round DOWN to the nearest quarter inch: 45.43 → 45.25, 45.87 → 45.75.
 *
 * NEVER up, and never to-nearest. A rail cut short can be packed out or
 * re-cut; a rail cut long does not fit between the uprights and is scrap.
 *
 * The epsilon covers binary floating point only: 48 - 1.66 * 2 + 0.75 can land
 * a hair under an exact quarter, and a bare floor would then drop a legitimate
 * 45.25 to 45. It is a millionth of an inch — far below anything a shop can
 * measure — so it cannot turn a real number into a longer cut.
 */
function floorToQuarter(inches) {
  const value = parseFloat(inches) || 0;
  if (value <= 0) return 0;
  return Math.floor(value * 4 + 1e-6) / 4;
}

// Uprights run 1" proud of the rails at each end, so the rail-to-rail span is
// the upright length less those two inches.
const UPRIGHT_PROUD_IN = 1;

// Diagonal bracing tube, shared by swing leaves and cantilevers.
const DIAGONAL_OD_IN = 1.625;

// A leaf taller than this gets a horizontal mid rail.
const MID_RAIL_MIN_HEIGHT_IN = 72;
// Vertical mid rails are a COUNT, not a yes/no:
//   leaf <= 96"  (8 ft)          -> none
//   96" < leaf <= 180" (8-15 ft) -> one, centred
//   leaf > 180" (over 15 ft)     -> TWO, at the thirds
const VERTICAL_MID_RAIL_MIN_WIDTH_IN = 96;
// Over 15 ft a leaf gets the second vertical AND diagonal bracing.
const DIAGONALS_MIN_WIDTH_IN = 180;
// Ceilings for an overridden member count. Not rules — just a guard so a typo
// or a paste cannot ask for forty rails.
const MAX_HORIZONTAL_RAILS = 6;
const MAX_VERTICAL_MEMBERS = 4;

// Uprights are cut 1.5" short on gates this height or under.
export const SHORT_GATE_MAX_HEIGHT_IN = 48;
const SHORT_GATE_DEDUCT_IN = 1.5;

// Barbed wire adds a fixed 12" of upright above the fence, carrying 3 strands
// mounted straight up (no angled arms). The extension never varies with height,
// and it does NOT change the horizontal rail cut.
const BARB_EXTENSION_IN = 12;
// A 2x4 is nominally 1.5" x 3.5". Bolted flat to a rail, the 3.5" face is what
// shows in elevation, so that is the figure the drawing needs.
const NAILER_FACE_IN = 3.5;
const BARB_STRAND_COUNT = 3;

// ── Cantilever slide gates ──
// Tube sizes are FIXED for cantilevers — they do not vary by grade the way a
// swing gate's frame OD does.
const CANT_RAIL_OD_IN = 2.5;      // top and bottom rails
const CANT_UPRIGHT_OD_IN = 2;     // vertical uprights

// The counterbalance tail is half the opening, capped at 12 ft.
const CANT_COUNTERBALANCE_RATIO = 0.5;
const CANT_COUNTERBALANCE_MAX_IN = 144;

// For DRAWING purposes only, 6" is transferred from the counterbalance to the
// opening. The total length is unaffected — this only shifts where the split is
// drawn, it is not a fabrication change.
const CANT_DRAW_SHIFT_IN = 6;

// End uprights are cut 10" shorter than the gate height.
const CANT_END_UPRIGHT_DEDUCT_IN = 10;

// Uprights are spaced evenly with no bay wider than this.
const CANT_MAX_BAY_IN = 72;

const clamp0 = (n) => Math.max(0, n);

/**
 * Work out the frame member cut lengths for one gate leaf.
 *
 * `gate` is { type, openingIn, heightIn, grade }, plus optional flags, and on a
 * cantilever an optional `overallLengthIn` — a LABEL override that never
 * reaches the geometry (see overallLengthOverrideIn below).
 *   type  — 'Single Swing' | 'Double Swing' (anything else counts as single)
 *   grade — 'Residential' | 'Commercial' (drives frame OD)
 *
 * Rail cut length is taken off the OPENING by a deduction that depends on the
 * frame OD and whether the opening is split between two leaves. Upright cut
 * length is the gate height, less 1.5" on gates 48" and under.
 *
 * Construction: the two uprights are the side members and stand 1" proud of
 * the rails top and bottom; the rails weld between them. So the frame's
 * overall width is the rail cut plus one frame OD at each side.
 *
 * Every length is clamped at 0 — an opening smaller than its own deduction
 * would otherwise return a negative cut.
 */
export function computeGateCuts(gate) {
  const type = gate?.type;
  const openingIn = parseFloat(gate?.openingIn) || 0;
  const heightIn = parseFloat(gate?.heightIn) || 0;

  const leafCount = type === 'Double Swing' ? 2 : 1;
  const isDouble = leafCount === 2;
  const isCantilever = type === 'Cantilever';

  // ACTUAL SIZE — swing gates only. The salesperson has the exact cut sizes
  // already, so NO formula runs: the entered width is the per-leaf rail cut
  // and the entered height is the upright cut, verbatim. On a double that
  // width is PER LEAF, not a total opening to halve. Deliberately ignored on a
  // cantilever, which always uses its own formulas.
  const actualSize = !isCantilever && !!gate?.actualSize;

  // CUSTOMER COPY — a sheet the customer signs to approve how the gate will
  // LOOK, not a sheet the shop builds from. It changes NOTHING about the cuts;
  // the flag rides on the cuts object purely so the sheet and the drawing can
  // both read one source and hide the shop-only detail in step.
  const customerCopy = !!gate?.customerCopy;

  // FRAME ONLY — a welded frame with no chain link in it. Swing gates only,
  // forced false on a cantilever exactly as actualSize is. It changes NOTHING
  // about the fabrication: every cut, the mid rail, the vertical mid rails, the
  // diagonals and the barbed wire are worked out the same way. The mesh is
  // simply not installed, so this drives rendering and the data block alone.
  const frameOnly = !isCantilever && !!gate?.frameOnly;

  // 2x4 NAILERS — wooden boards bolted to the horizontal rails of a bare frame
  // so wood or vinyl can be attached later. Only meaningful on a FRAME ONLY
  // swing gate: there is nowhere to bolt a board on a gate that already carries
  // mesh, and a cantilever is never built this way. frameOnly is already false
  // on a cantilever, so gating on it covers both exclusions at once.
  // Like frameOnly, this changes NOTHING about the cuts.
  const nailers2x4 = frameOnly && !!gate?.nailers2x4;

  // ── NAILER ON THE MID RAIL ──
  // Boards go on the top rail, the bottom rail and the mid rail. Wood infill
  // wants all three; PVC wants top and bottom only, so the mid rail's board is
  // its own switch.
  //
  // DEFAULT TICKED — wood is the common case — which also means every drawing
  // saved before this field existed keeps all three boards: only an explicit
  // `false` removes one. Where there are several intermediate rails (a
  // horizontal override above 3), this one flag covers them all: they are the
  // same board in the same role.
  const nailerMidRail = gate?.nailerMidRail !== false;

  // FULL HEIGHT — build a short gate at its stated height instead of taking the
  // 1.5" off. Swing gates only. This can ONLY ever matter at 48" and under:
  // taller gates have no deduction to skip, so the flag is inert there rather
  // than wrong. It is also inert under actual size, which already takes the
  // entered height verbatim. Unlike actual size it leaves the WIDTH formula
  // running, which is the whole reason it exists as a separate switch.
  const fullHeight = !isCantilever && !!gate?.fullHeight;

  // QUANTITY — how many of this gate are being built. A count of physical
  // gates, so at least one and whole numbers only; anything else clamps rather
  // than propagating. It changes NO calculation: every cut on this sheet is
  // per gate and identical whether one or ten are made. It exists so the sheet
  // can say so.
  const quantity = Math.max(1, Math.round(parseFloat(gate?.quantity) || 1));

  // OPENING SIZE WARNING — for the customer's copy of a swing gate only.
  //
  // A swing gate is built to FIT an opening, so the finished gate is narrower
  // than the number the customer gave, and customers read the opening figure as
  // the gate's size. Two exclusions, both because the warning would otherwise
  // be false rather than merely unnecessary:
  //   - a CANTILEVER spans its opening rather than fitting inside it, so its
  //     finished size is not reduced;
  //   - under ACTUAL SIZE no deduction is applied at all — the gate really is
  //     cut to the entered figure.
  const showOpeningWarning = customerCopy && !isCantilever && !actualSize;
  const frameOdIn = FRAME_OD_IN[gate?.grade] ?? DEFAULT_FRAME_OD_IN;

  // Rail cut — deduction depends on frame OD and leaf count, unless actual
  // size is on, in which case the entered figure IS the cut.
  let railCutIn;
  if (actualSize) {
    railCutIn = openingIn;
  } else if (frameOdIn === 1.625) {
    railCutIn = isDouble ? (openingIn - 13) * 0.5 : openingIn - 6.75;
  } else {
    // SHOP RULE, RESIDENTIAL DOUBLE SWING ONLY: this one branch rounds the rail
    // cut UP to the next whole inch. It is NOT a general rounding policy — every
    // other branch keeps its exact fractional value (a commercial double still
    // cuts to 65.5", a commercial single to 41.25"), and the upright cut is
    // never rounded at all (48" gates must still return 46.5"). Do not
    // generalise this to other grades, to single gates, or to the uprights.
    railCutIn = isDouble ? Math.ceil((openingIn - 10.5) * 0.5) : openingIn - 6;
  }
  // Rounded before railToRailIn and overallWidthIn are derived below, so the
  // drawn geometry and the printed label are always the same number.
  railCutIn = clamp0(railCutIn);

  // Upright cut — short gates lose 1.5"; barbed wire then adds a flat 12".
  const barbedWire = !!gate?.barbedWire;
  // Actual size takes the height verbatim — no short-gate deduction. Barbed
  // wire still adds its 12in below, because that is a physical addition to the
  // member rather than a deduction the formula was making.
  // Full height skips the short-gate deduction and nothing else — the rail cut
  // above has already been worked out and is untouched by this.
  const baseUprightIn = clamp0(
    (actualSize || fullHeight)
      ? heightIn
      : (heightIn <= SHORT_GATE_MAX_HEIGHT_IN ? heightIn - SHORT_GATE_DEDUCT_IN : heightIn)
  );
  const barbExtensionIn = barbedWire ? BARB_EXTENSION_IN : 0;
  const uprightCutIn = clamp0(baseUprightIn + barbExtensionIn);

  // Rail-to-rail is measured from the BASE upright, not the extended one: the
  // barbed-wire extension rises above the top rail, so it does not push the
  // rails apart. Deriving this from uprightCutIn would overstate the span by
  // the full 12".
  const railToRailIn = clamp0(baseUprightIn - UPRIGHT_PROUD_IN * 2);
  const overallWidthIn = clamp0(railCutIn + 2 * frameOdIn);

  // ── Rail cut piece — ACTUAL SIZE swing gates only ──
  //
  // WIDTH ONLY. It takes nothing from the height and changes no other cut: the
  // upright cut, the rail-to-rail span and every member rule above are worked
  // out exactly as they were, and this is derived from the figure already
  // printed rather than replacing it.
  //
  // Not computed for a normal (non-actual-size) gate: there the rail cut IS
  // already the deducted figure the welder cuts to, so a second deduction would
  // be wrong. actualSize is false on a cantilever, so this is inert there too.
  //
  // On a DOUBLE the entered width is per leaf (see actualSize above), so this
  // cut is per leaf as well — the sheet says so beside the number.
  // SHOP COPY ONLY, and that is enforced on the NUMBER, not just on a flag the
  // renderer is trusted to check. The customer's copy keeps exactly what it
  // prints today — the entered width — and a fabrication length has no business
  // on a sheet somebody signs to approve how the gate will look. Returning 0
  // means a future renderer that reaches for railCutPieceIn directly still
  // cannot put it on a customer copy.
  const cutPieceSpec = RAIL_CUT_PIECE_BY_FRAME_OD[frameOdIn] || null;
  const railCutPieceIn = (actualSize && !customerCopy && cutPieceSpec)
    ? floorToQuarter(clamp0(railCutIn - cutPieceSpec.realOdIn * 2 + cutPieceSpec.notchIn))
    : 0;
  const showRailCutPiece = railCutPieceIn > 0;

  // Member rules.
  //
  // THE RULES THEMSELVES ARE UNCHANGED. What is new below is that their RESULT
  // can be overridden per drawing — see the override block after them. The rule
  // value is always computed and always exported, so the form can show it as
  // the starting point and offer a way back to it.
  const hasMidRailByRule = heightIn >= MID_RAIL_MIN_HEIGHT_IN;
  // Bracing is judged PER LEAF, so a double splits the opening first.
  // Under actual size the entered width already IS the leaf width, so it is not
  // halved for a double.
  const leafWidthNominalIn = actualSize
    ? openingIn
    : (isDouble ? openingIn / 2 : openingIn);
  // One vertical over 8 ft, a second over 15 ft — so a very wide leaf is split
  // into thirds rather than halves.
  const verticalRuleCount = leafWidthNominalIn > DIAGONALS_MIN_WIDTH_IN
    ? 2
    : leafWidthNominalIn > VERTICAL_MID_RAIL_MIN_WIDTH_IN
      ? 1
      : 0;
  // Two rails always, plus the mid rail when the height rule calls for one.
  const horizontalRuleCount = hasMidRailByRule ? 3 : 2;

  // ── OVERRIDING A COUNT ─────────────────────────────────────────────────
  //
  // Sometimes a customer wants a member the rules do not call for, or does not
  // want one they do. One control per member type handles both directions: it
  // starts at the rule's number and can be moved either way.
  //
  // null / absent / unparseable means "use the rule" — which is every drawing
  // saved before this existed, so none of them changes.
  //
  // SWING GATES ONLY. A cantilever's members come from its own bay layout and
  // are deliberately untouched by this.
  //
  // BOTH LEAVES OF A DOUBLE get the same count. The two leaves are one gate and
  // have always been drawn identically; independent counts would be a different
  // feature and a different sheet.
  //
  // CUT LENGTHS ARE UNAFFECTED, and that is the point: an extra rail is another
  // piece of the SAME rail cut, and an extra vertical another piece of the same
  // vertical cut. What changes is how many pieces the welder cuts, so the sheet
  // states the counts (and the length each is cut to) whenever they are
  // overridden — see overriddenMembers below and the note GateSheet prints.
  const countOverride = (value, max) => {
    if (isCantilever) return null;
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.min(max, Math.round(n));
  };
  const horizontalOverride = countOverride(gate?.horizontalRailCount, MAX_HORIZONTAL_RAILS);
  const verticalOverride = countOverride(gate?.verticalMemberCount, MAX_VERTICAL_MEMBERS);

  const horizontalRailCount = horizontalOverride !== null ? horizontalOverride : horizontalRuleCount;
  const verticalMidRailCount = verticalOverride !== null ? verticalOverride : verticalRuleCount;
  // "Is there a mid rail" now means "is there an intermediate horizontal rail",
  // which an override can create on a short gate or remove from a tall one. The
  // nailers and the diagonals' upper boundary both read this.
  const hasMidRail = horizontalRailCount >= 3;
  const horizontalOverridden = horizontalOverride !== null && horizontalOverride !== horizontalRuleCount;
  const verticalOverridden = verticalOverride !== null && verticalOverride !== verticalRuleCount;

  // DIAGONALS FOLLOW THE WIDTH RULE, NOT THE OVERRIDE. There is no control for
  // them: over 15 ft a leaf is braced, and that is a structural decision the
  // office does not take on the sheet. The existing condition that they are
  // drawn only where the two verticals they brace INTO exist is unchanged — so
  // overriding the verticals away on a very wide leaf takes the diagonals with
  // them, which the override note on the sheet makes visible.
  const hasDiagonals = leafWidthNominalIn > DIAGONALS_MIN_WIDTH_IN;

  // The vertical members' cut length: rail-to-rail, less the two rails they
  // weld between. Exported for the override note, which has to tell the welder
  // what to cut the extra pieces to.
  const verticalCutIn = clamp0(railToRailIn - 2 * frameOdIn);

  // ── Cantilever slide gate ──
  // A cantilever is a single sliding leaf with a counterbalance tail, so almost
  // none of the swing rules above apply to it. Everything below is inert for a
  // swing gate: every cantilever figure is 0/null, so swing output is
  // unaffected.

  const counterbalanceIn = isCantilever
    ? Math.min(openingIn * CANT_COUNTERBALANCE_RATIO, CANT_COUNTERBALANCE_MAX_IN)
    : 0;
  const totalLengthIn = isCantilever ? openingIn + counterbalanceIn : 0;

  // ── OVERALL LENGTH: A TYPED NUMBER IS A LABEL, NEVER A LENGTH ──
  //
  // The owner can override the overall length PRINTED above a cantilever's
  // frame (`gate.overallLengthIn`; null, empty or not a positive number means
  // "use the computed total").
  //
  // ⚠️ THAT OVERRIDE CHANGES THE LABEL AND NOTHING ELSE. totalLengthIn above
  // stays the computed opening + counterbalance, and it — not the override —
  // drives the frame length, the bay layout, the mesh extent and every cut
  // below. A typed 28' does not shrink a 30' frame.
  //
  // So the override deliberately never comes back out as a length. What is
  // returned is the label to print, the computed label beside it, and whether
  // the two disagree — nothing a renderer could mistake for geometry.
  const typedOverallIn = parseFloat(gate?.overallLengthIn);
  const overallLengthOverrideIn = isCantilever && Number.isFinite(typedOverallIn) && typedOverallIn > 0
    ? typedOverallIn
    : null;
  // Compared to the cent, the precision the label itself prints at.
  const overallLengthDiverges = overallLengthOverrideIn !== null
    && round2(overallLengthOverrideIn) !== round2(totalLengthIn);

  // The 6" shift moves the drawn split only — totalLengthIn is untouched, so
  // the gate the shop builds is the same either way.
  const drawnOpeningIn = isCantilever ? openingIn + CANT_DRAW_SHIFT_IN : 0;
  const drawnCounterbalanceIn = isCantilever ? clamp0(counterbalanceIn - CANT_DRAW_SHIFT_IN) : 0;

  // Only the END uprights are called out. Interior uprights, rails and
  // diagonals are deliberately NOT computed or exposed — the shop does not want
  // those numbers on the sheet.
  const endUprightCutIn = isCantilever ? clamp0(heightIn - CANT_END_UPRIGHT_DEDUCT_IN) : 0;

  // Bays are laid out PER SECTION. The opening and the counterbalance each get
  // their own even division, neither exceeding 72" — they are NOT spread across
  // the gate as one run. A 240" opening with a 120" tail is 4 bays of 60 plus 2
  // bays of 60; the two sections only happen to match there.
  //
  // The TRUE openingIn and counterbalanceIn drive this. The 6" draw shift moves
  // the dimension label only and must never reach the geometry.
  const openingBayCount = isCantilever && openingIn > 0
    ? Math.ceil(openingIn / CANT_MAX_BAY_IN)
    : 0;
  const openingBaySpacingIn = openingBayCount > 0 ? openingIn / openingBayCount : 0;
  const cbBayCount = isCantilever && counterbalanceIn > 0
    ? Math.ceil(counterbalanceIn / CANT_MAX_BAY_IN)
    : 0;
  const cbBaySpacingIn = cbBayCount > 0 ? counterbalanceIn / cbBayCount : 0;

  // Upright positions in inches from the opening end of the frame, so the
  // renderer never recomputes the layout. The upright at `openingIn` is shared:
  // it closes the last opening bay and opens the first counterbalance bay.
  const uprightPositionsIn = [];
  if (isCantilever) {
    for (let i = 0; i <= openingBayCount; i += 1) {
      uprightPositionsIn.push(i * openingBaySpacingIn);
    }
    for (let j = 1; j <= cbBayCount; j += 1) {
      uprightPositionsIn.push(openingIn + j * cbBaySpacingIn);
    }
  }

  // Automatic gates are sheeted across the whole gate for UL325; manual gates
  // only over the opening.
  const operation = gate?.operation === 'Automatic' ? 'Automatic' : 'Manual';
  const isAutomatic = isCantilever && operation === 'Automatic';
  // Only the MESH follows Manual vs Automatic. Barbed wire always spans the
  // opening portion only, on every cantilever regardless of operation.
  const meshCoversCounterbalance = isAutomatic;

  // Barb arms always lean the same way the gate slides, on BOTH hang sides.
  // hangSide changes only which FACE the wire sits on: Outside is the near
  // face, Inside the far face. The renderer offsets and lightens the far-face
  // assembly; the lean itself is identical either way.
  const slideDirection = gate?.slideDirection === 'Left' ? 'Left' : 'Right';
  const hangSide = gate?.hangSide === 'Inside' ? 'Inside' : 'Outside';
  const barbLeanDirection = isCantilever ? slideDirection : null;

  return {
    leafCount,
    actualSize,
    customerCopy,
    frameOnly,
    nailers2x4,
    fullHeight,
    quantity,
    showOpeningWarning,
    // Drawn face width of a nailer, in inches. Lives here beside diagonalOdIn
    // so the renderer never hard-codes a material dimension.
    nailerFaceIn: NAILER_FACE_IN,
    frameOdIn,
    railCutIn,
    uprightCutIn,
    railToRailIn,
    overallWidthIn,
    hasMidRail,
    hasDiagonals,
    // EFFECTIVE counts — the rule, or the override when there is one. The
    // renderer draws these and asks no questions.
    verticalMidRailCount,
    horizontalRailCount,
    // The rules' own values, for the form to show as the starting point.
    horizontalRuleCount,
    verticalRuleCount,
    horizontalOverridden,
    verticalOverridden,
    membersOverridden: horizontalOverridden || verticalOverridden,
    verticalCutIn,
    verticalCutLabel: formatInches(verticalCutIn),
    // Whether the mid rail carries a 2x4. Only meaningful with nailers on and
    // an intermediate rail to put one on.
    nailerMidRail,
    barbedWire,
    barbExtensionIn,
    barbStrandCount: barbedWire ? BARB_STRAND_COUNT : 0,
    // ── Cantilever ── (all inert/zero for swing gates)
    isCantilever,
    railOdIn: isCantilever ? CANT_RAIL_OD_IN : null,
    uprightOdIn: isCantilever ? CANT_UPRIGHT_OD_IN : null,
    diagonalOdIn: DIAGONAL_OD_IN,
    slideDirection: isCantilever ? slideDirection : null,
    hangSide: isCantilever ? hangSide : null,
    operation: isCantilever ? operation : null,
    counterbalanceIn,
    totalLengthIn,
    drawnOpeningIn,
    drawnCounterbalanceIn,
    endUprightCutIn,
    openingBayCount,
    openingBaySpacingIn,
    cbBayCount,
    cbBaySpacingIn,
    uprightPositionsIn,
    meshCoversCounterbalance,
    barbLeanDirection,
    // The counterbalance deliberately has NO label — the shop does not dimension
    // it. The height appears only in the data block, never on the drawing.
    drawnOpeningLabel: isCantilever ? formatFeetInches(drawnOpeningIn) : null,
    // Overall length, printed above the frame. LABELS ONLY — see the note at
    // overallLengthOverrideIn. overallLengthLabel is what the sheet prints (the
    // typed figure if there is one); computedOverallLengthLabel is always the
    // formula's, for the form to show beside it.
    overallLengthLabel: isCantilever
      ? formatFeetInches(overallLengthOverrideIn !== null ? overallLengthOverrideIn : totalLengthIn)
      : null,
    computedOverallLengthLabel: isCantilever ? formatFeetInches(totalLengthIn) : null,
    overallLengthOverrideIn,
    overallLengthDiverges,
    endUprightLabel: isCantilever
      ? `Cut ${formatInches(CANT_UPRIGHT_OD_IN)} x ${formatInches(endUprightCutIn)}`
      : null,
    railCutLabel: formatInches(railCutIn),
    uprightCutLabel: formatInches(uprightCutIn),
    // The welder's rail length under actual size — see railCutPieceIn above.
    // 0 / null / false on every other kind of sheet, so a renderer that just
    // asks for it draws nothing rather than needing to know the rules.
    railCutPieceIn,
    showRailCutPiece,
    railCutPieceLabel: showRailCutPiece ? formatInches(railCutPieceIn) : null,
    // Outside-to-outside width label. DELIBERATELY limited to ACTUAL SIZE
    // gates, and null everywhere else: actual size is the only mode in which
    // the user entered a finished outside measurement, so it is the only mode
    // in which an OTO figure is theirs rather than one derived behind their
    // back. A normal swing gate is dimensioned across the RAIL instead — the
    // welder's job is a rail cut, and the deduction that produced it is the
    // point of the drawing. (actualSize is already false on a cantilever, so
    // this is null there too.)
    //
    // The number is the figure the user ENTERED (the rail cut), not the
    // derived outside width: they typed it on purpose and have to see it back.
    otoLabel: actualSize ? `${formatInches(railCutIn)} OTO` : null,
    openingLabel: formatInches(openingIn),
    heightLabel: formatInches(heightIn),
    // Split labels for the barbed-wire dimensions: the sheet reads 6' + 1',
    // not a single 84" figure.
    fenceHeightLabel: formatHeightLabel(heightIn),
    barbHeightLabel: barbedWire ? formatHeightLabel(BARB_EXTENSION_IN) : null,
  };
}
